import React from 'react';
import styles from '../BranchDrawer.module.css';
import Ico from '../../common/Ico';
import { ICO_CHECK, ICO_EXPAND, ICO_SKIP, ICO_XMARK } from './constants';
import { isVulnerabilityOperation } from './helpers';

function titleCaseKey(key) {
  return String(key)
    .replace(/[_-]/g, ' ')
    .toLowerCase()
    .replace(/\b\w/g, c => c.toUpperCase());
}

function formatOutcomeValue(value) {
  if (typeof value === 'number') return value.toLocaleString();
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return String(value);
}

const OUTCOME_KEY_ORDER = [
  'expected', 'unexpected', 'flaky', 'skipped',
  'passed', 'failed', 'success', 'errors', 'warnings',
  'CRITICAL', 'HIGH', 'MEDIUM', 'LOW',
  'critical', 'high', 'medium', 'low',
];

const OUTCOME_EXCLUDED_KEYS = new Set([
  'duration',
  'durationMs',
  'duration_ms',
  'elapsed',
  'elapsedMs',
  'elapsed_ms',
  'startTime',
  'start_time',
  'startedAt',
  'started_at',
  'endTime',
  'end_time',
  'completedAt',
  'completed_at',
]);

const OUTCOME_KIND_BY_KEY = {
  expected: 'ok',
  passed: 'ok',
  success: 'ok',
  unexpected: 'bad',
  failed: 'bad',
  errors: 'bad',
  CRITICAL: 'bad',
  critical: 'bad',
  HIGH: 'bad',
  high: 'bad',
  flaky: 'warn',
  warnings: 'warn',
  MEDIUM: 'warn',
  medium: 'warn',
  skipped: 'neutral',
  LOW: 'neutral',
  low: 'neutral',
};

const OUTCOME_ICON_BY_KIND = {
  ok: ICO_CHECK,
  bad: ICO_XMARK,
  warn: ICO_EXPAND,
  neutral: ICO_SKIP,
};

function isOmdOutcomeStep(op) {
  const key = `${op?.key || ''} ${op?.name || ''} ${op?.operation || ''}`.toLowerCase();
  return key.includes('omd') || key.includes('omd_e2e');
}

function outcomeEntries(op) {
  if (!op || isOmdOutcomeStep(op)) return [];
  const outcome = op.outcome || op.counts || op.result?.aut_outcome || op.result?.counts;
  if (!outcome || typeof outcome !== 'object' || Array.isArray(outcome)) return [];
  const entries = Object.entries(outcome)
    .filter(([key, value]) =>
      !OUTCOME_EXCLUDED_KEYS.has(key) &&
      value !== null &&
      value !== undefined &&
      typeof value !== 'object'
    );
  return entries.sort(([a], [b]) => {
    const ia = OUTCOME_KEY_ORDER.indexOf(a);
    const ib = OUTCOME_KEY_ORDER.indexOf(b);
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib) || a.localeCompare(b);
  });
}

export function AutOutcomeInline({ outcome }) {
  if (!outcome || outcome.expected == null) return null;
  return (
    <span className={styles.autInline}>
      <span className={styles.statOk}>
        <Ico d={ICO_CHECK} size={11} strokeWidth={3} />
        {outcome.expected.toLocaleString()}
      </span>
      {outcome.unexpected > 0 && (
        <span className={styles.statErr}>
          <Ico d={ICO_XMARK} size={10} strokeWidth={3} />
          {outcome.unexpected}
        </span>
      )}
      {outcome.flaky > 0 && (
        <span className={styles.statFlaky}>
          <Ico d={ICO_EXPAND} size={10} strokeWidth={2.4} />
          {outcome.flaky}
        </span>
      )}
      {outcome.skipped > 0 && (
        <span className={styles.statSkipped}>
          <Ico d={ICO_SKIP} size={10} strokeWidth={2} />
          {outcome.skipped}
        </span>
      )}
    </span>
  );
}

export default function OutcomeSummary({ op }) {
  const entries = outcomeEntries(op);
  if (!entries.length) return null;
  const noFixCounts = isVulnerabilityOperation(op) ? (op?.result?.counts_no_fix || {}) : {};
  return (
    <div className={styles.sectionGroup}>
      <div className={styles.overline}>Outcome</div>
      <div className={styles.outcomeGrid}>
        {entries.map(([key, value]) => {
          const noFix = Number(noFixCounts[key] || 0);
          const allNoFix = noFix > 0 && noFix >= Number(value);
          const kind = allNoFix ? 'ok' : (OUTCOME_KIND_BY_KEY[key] || 'neutral');
          return (
            <div key={key} className={`${styles.outcomeCard} ${styles[`outcome_${kind}`]}`}>
              <span className={styles.outcomeIcon}>
                <Ico d={OUTCOME_ICON_BY_KIND[kind]} size={12} strokeWidth={2.4} />
              </span>
              <span className={styles.outcomeText}>
                <span className={styles.outcomeLabel}>{titleCaseKey(key)}</span>
                <span className={styles.outcomeValue}>
                  {formatOutcomeValue(value)}
                  {noFix > 0 && <span className={styles.outcomeNote}> ({noFix} no fix)</span>}
                </span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}
